import { getToken } from "./token";
import type {
  AIAskView,
  AISummary,
  AccountCohort,
  AccountCohortsResponse,
  AccountBalanceResponse,
  AccountsResponse,
  CommandsAccepted,
  CommandTarget,
  DecidedSignal,
  DecidedSource,
  BookUpdate,
  CexDepthResponse,
  CCFeedResponse,
  CommandAccepted,
  CounterpartiesResponse,
  Envelope,
  FollowMode,
  FollowModeState,
  StrategyMode,
  StrategySettings,
  StrategySettingsPatch,
  FollowSignal,
  FollowSummary,
  FlowWindow,
  KillSwitches,
  SubsystemAccounts,
  SubsystemAccountsPatch,
  LockKind,
  LockMode,
  LockSettings,
  LockSignalsResponse,
  MarketWalletTradesResponse,
  CohortBucket,
  ConsensusBin,
  ConsensusCell,
  ConsensusTakerBucket,
  ConsensusTakerReport,
  ConsensusSignal,
  ShadowShape,
  CohortSignal,
  FamilyLink,
  FamilyRoster,
  FamilySignal,
  FamilyRevRows,
  FamilySummary,
  MispricedGridResponse,
  MispricedGroup,
  MispricedSignal,
  MispricedSlice,
  OpsAlertsResponse,
  Market,
  OrdersResponse,
  ParticipantCohortKind,
  TimelineResponse,
  ParticipantsResponse,
  PlaceOrderRequest,
  PlacePaperOrderRequest,
  MarketPnlResponse,
  PositionsResponse,
  RebatesResponse,
  ServiceStatus,
  TradesResponse,
  TapeStatsResponse,
  ValuationResponse,
  WalletResponse,
  WalletTradesResponse,
  WalletTagsResponse,
  WalletTagsApplyResponse,
  WarmSizesRequest,
  MakerArmResponse,
  WarmSizesResponse,
  PairRoleSummary,
  PairSignal,
  ShiftCell,
  ShiftSignal,
  ShiftSlice,
  PFBookResponse,
  PFCancelResult,
  PFOrder,
  PFPosition,
  PFPlaceRequest,
  PFOutcome,
  PFPlaceResult,
  PFStatus,
} from "./types";

/**
 * Ошибка HTTP с кодом ответа.
 *
 * Код нужен, потому что 404 по стакану — это не сбой, а состояние интерфейса:
 * ключ book:<asset_id> живёт 30 с, и его отсутствие означает «трейдер не
 * публикует этот стакан». Отличить это от настоящей ошибки можно только по коду.
 */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/**
 * REST-клиент web_api.
 *
 * Токен оператора хранится в localStorage (см. token.ts) — в MVP один общий
 * токен на оператора, полноценная аутентификация появится после MVP.
 */

/**
 * Токен кодируется в base64url — так же, как для WebSocket-субпротокола (ws.ts),
 * и по родственной причине: значение HTTP-заголовка ограничено ISO-8859-1, а
 * токен по .env.example может содержать любые символы. С кириллицей или эмодзи
 * сырое значение роняло бы каждый fetch TypeError'ом ещё до отправки, при
 * работающем WebSocket. web_api принимает и кодированную, и сырую форму.
 */
function encodeToken(token: string): string {
  const bytes = new TextEncoder().encode(token);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

/**
 * Заголовки — Record, а не HeadersInit: их приходится РАСШИРЯТЬ (Content-Type
 * для тела), а HeadersInit это юнион из трёх форм, одну из которых нельзя ни
 * разложить, ни дополнить.
 */
function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${encodeToken(token)}` } : {};
}

/**
 * Общий транспорт REST.
 *
 * Тело передаётся условным спредом, а не `body: body ?? undefined`: при
 * exactOptionalPropertyTypes «свойства нет» и «свойство равно undefined» —
 * разные вещи, и второе RequestInit не принимает.
 *
 * Ответ разбирается ВСЕГДА: все обработчики web_api отвечают телом, включая 202
 * и 4xx. Появится 204 — здесь потребуется ветка, и упасть на ней заметно лучше,
 * чем молча вернуть undefined как T.
 */
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers = authHeaders();
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const parsed = (await res.json().catch(() => ({}))) as { error?: string };
    throw new HttpError(res.status, parsed.error ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

async function get<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

/** Строка запроса из непустых параметров. */
function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : "";
}

/**
 * Кусочек query со срезом по ДЛИТЕЛЬНОСТИ ОКНА серии.
 *
 * Отдельной функцией, а не полем в каждом вызове: параметр глобальный — им
 * режутся все семь журналов страницы сигналов разом, — и семь копий условия
 * «ноль означает все окна» разъехались бы на первой правке.
 *
 * Ноль и `undefined` дают ПУСТО, а не `window_len_s=0`: снятый фильтр обязан
 * давать ровно тот запрос, который уходил до его появления, иначе разницу
 * «до и после» пришлось бы искать в сервере.
 *
 * `window_len_s`, а не `window_s`: второе на сервере значит ширину метрики
 * резолва (TWAP-30/60), и параметр с таким именем резал бы не то.
 */
function winQS(windowLen?: number): { window_len_s?: number } {
  return windowLen ? { window_len_s: windowLen } : {};
}

export const api = {
  status: () => get<ServiceStatus>("/status"),
  markets: () => get<{ markets: Market[] }>("/markets"),
  market: (conditionID: string) =>
    get<{ market: Market }>(`/markets/${encodeURIComponent(conditionID)}`),
  /**
   * Снапшот стакана из ключа book:<asset_id>.
   *
   * Отдаётся ТОТ ЖЕ конверт, что летит в сокет, поэтому и ключ, и поток
   * разбираются одним кодом (см. fromSnapshot в store/book.ts).
   */
  book: (assetID: string) =>
    get<Envelope<BookUpdate>>(`/book/${encodeURIComponent(assetID)}`),

  // --- торговля (веха M4) --------------------------------------------------

  orders: (params?: { status?: "open" | "all"; market?: string; limit?: number }) =>
    get<OrdersResponse>(`/orders${qs(params)}`),
  /**
   * Состав аккаунтов набора: sidecar сервиса и поверх — применённое трейдером.
   * Адресов выхода в ответе нет и быть не может (инвариант 2).
   */
  accounts: () => get<AccountsResponse>("/accounts"),
  accountBalance: (account: string) =>
    get<AccountBalanceResponse>(`/accounts/balance${qs({ account })}`),
  /** Когорты аккаунтов (T5.5). */
  accountCohorts: () => get<AccountCohortsResponse>("/account-cohorts"),
  /**
   * Заменить список когорт ЦЕЛИКОМ: так проще отказ web_api — одна проверка
   * всего списка, без когорты, наполовину записанной.
   */
  setAccountCohorts: (cohorts: Pick<AccountCohort, "name" | "accounts">[]) =>
    request<AccountCohortsResponse>("PUT", "/account-cohorts", { cohorts }),
  /**
   * Цель — отдельным ОБЯЗАТЕЛЬНЫМ параметром, а не полем запроса: забыть её
   * не даст компилятор, а умолчания «единственный аккаунт» нет нигде.
   */
  placeOrder: (req: PlaceOrderRequest, target: CommandTarget) =>
    request<CommandsAccepted>("POST", "/orders", { ...req, ...target }),
  /**
   * Снятие ордера. Аккаунт web_api берёт из строки ордера; account в запросе —
   * запасной путь на случай, когда строки в базе ещё нет (таблица отстаёт от
   * биржи), поэтому он едет всегда, когда известен.
   */
  cancelOrder: (clientOrderID: string, account?: string) =>
    request<{ command: CommandAccepted }>(
      "DELETE",
      `/orders/${encodeURIComponent(clientOrderID)}${qs({ account })}`,
    ),
  /**
   * Снятие всех ордеров или всех по одному рынку.
   *
   * Тело уходит всегда, пустым объектом при отсутствии рынка: web_api принимает
   * и запрос без тела, но явный `{}` не даёт случайно превратить «снять по
   * рынку» в «снять всё» при опечатке в вызывающем коде.
   */
  /**
   * Пробный (dry-only) ордер. ОТДЕЛЬНЫЙ путь, а не флаг в теле настоящего:
   * поле, от которого зависит, тратятся ли деньги, при опечатке отказывает в
   * сторону траты, а отдельный маршрут — в обратную (ADR-0031).
   */
  placePaperOrder: (req: PlacePaperOrderRequest, target: CommandTarget) =>
    request<CommandsAccepted>("POST", "/paper/orders", { ...req, ...target }),

  /** Счёт своей позиции по окну: реальный и пробный режимы рядом. */
  marketPnl: (conditionID: string) =>
    get<MarketPnlResponse>(`/markets/${encodeURIComponent(conditionID)}/pnl`),

  /** Сброс симуляции окна. Настоящих сделок не касается: таблица другая. */
  resetPaper: (conditionID: string) =>
    request<{ removed: number }>(
      "DELETE",
      `/markets/${encodeURIComponent(conditionID)}/paper`,
    ),

  cancelAll: (target: CommandTarget, conditionID?: string) =>
    request<CommandsAccepted>(
      "POST",
      "/orders/cancel-all",
      conditionID ? { condition_id: conditionID, ...target } : { ...target },
    ),

  /**
   * Позиции оператора по рынку.
   *
   * market обязателен и на стороне web_api: у кошелька, торгующего
   * пятиминутные окна, погашенные позиции остаются в выдаче индексатора
   * навсегда, и «все позиции» — это сотни строк пыли.
   */
  positions: (conditionID: string, account: string) =>
    get<PositionsResponse>(`/positions${qs({ market: conditionID, account })}`),

  /**
   * Страйк, индекс, отклонение и справедливые цены исходов.
   *
   * Считает web_api: ему одному доступны обе половины — страйк из Postgres и
   * индекс из Redis. Модель на клиенте не дублируется намеренно, иначе TS и Go
   * разойдутся на первой правке, а расхождение видно только по деньгам.
   */
  valuation: (conditionID: string) =>
    get<ValuationResponse>(`/markets/${encodeURIComponent(conditionID)}/valuation`),

  /**
   * Счётчик объёма ленты рядом с числом самой биржи.
   *
   * Оба числа приходят ОДНИМ ответом, и это не экономия запроса: смысл имеет их
   * разница, а два источника одного факта разъехались бы по возрасту, и оператор
   * сравнивал бы свежее с несвежим, не зная об этом.
   */
  tape: (conditionID: string) =>
    get<TapeStatsResponse>(`/markets/${encodeURIComponent(conditionID)}/tape`),

  /**
   * Кто сколько набрал в рынке и какой ролью.
   *
   * Один ответ на оба блока панели — накопление сделками и держателей сейчас — и
   * это не экономия запроса: списки РАЗНЫЕ (замер: 227 держателей против 770
   * наторговавших), и расхождение по одному кошельку само есть сигнал. Разъедься
   * они по возрасту, и оператор сравнивал бы снимки разных моментов, не зная об
   * этом. Оттуда же приходят имена кошельков для обоих блоков.
   */
  participants: (
    conditionID: string,
    flow?: FlowWindow,
    against?: boolean,
    tags?: readonly string[],
    notags?: readonly string[],
    groups?: readonly ParticipantCohortKind[],
    notgroups?: readonly ParticipantCohortKind[],
  ) =>
    get<ParticipantsResponse>(
      `/markets/${encodeURIComponent(conditionID)}/participants` +
        // Окно уезжает параметром, только когда выбрано: умолчание живёт на
        // сервере, и дублировать его здесь значило бы менять числа у оператора
        // при любом расхождении двух копий одной константы. Режим — по той же
        // причине: выключенный он на проводе не появляется вовсе.
        //
        // Фильтр по пометкам уезжает НА СЕРВЕР, а не режется здесь, и это не
        // экономия трафика: сервер обрезает выдачу сотней крупнейших, и
        // помеченный кошелёк с маленьким нетто до этой стороны просто не доехал
        // бы. Тот же довод, что у режима «против нас».
        qs({
          flow,
          against: against ? "1" : undefined,
          tags: tags?.length ? tags.join(",") : undefined,
          notags: notags?.length ? notags.join(",") : undefined,
          // Фильтр по подгруппе — туда же и по тому же доводу: подгруппу считает
          // тот же проход, что сводку, по ВСЕМ кошелькам снимка, и режет он тоже
          // до отсечки в сотню.
          groups: groups?.length ? groups.join(",") : undefined,
          notgroups: notgroups?.length ? notgroups.join(",") : undefined,
        }),
    ),

  /**
   * Ход накопления во времени: ряд перевеса групп и ряд котировки.
   *
   * Отдельный запрос от панели, а не поле её ответа, и это не про удобство.
   * Панель обновляется стуком трейдера каждые сто миллисекунд, а ряд весит на
   * два порядка больше свода — уедь он тем же ответом, и цена стука выросла бы
   * во столько же раз на каждой открытой вкладке.
   *
   * Группа уезжает НА СЕРВЕР, а не режется здесь: линию группы считает тот же
   * проход по строкам, что и линию рынка, и посчитать её в браузере было бы
   * нечем — раздача кошельков по группам живёт на сервере.
   */
  timeline: (
    conditionID: string,
    filter: {
      tags?: readonly string[];
      notags?: readonly string[];
      groups?: readonly ParticipantCohortKind[];
      notgroups?: readonly ParticipantCohortKind[];
      wallets?: readonly string[];
    },
    step?: number,
  ) =>
    get<TimelineResponse>(
      `/markets/${encodeURIComponent(conditionID)}/timeline` +
        // Фильтр уезжает НА СЕРВЕР теми же параметрами, что у панели
        // накопления, и это не экономия трафика: состав считается по ВСЕМ
        // подходящим кошелькам снимка, а до этой стороны доезжает лишь сотня
        // строк таблицы. Отбери здесь — и ряд описывал бы верхушку выборки.
        qs({
          tags: filter.tags?.length ? filter.tags.join(",") : undefined,
          notags: filter.notags?.length ? filter.notags.join(",") : undefined,
          groups: filter.groups?.length ? filter.groups.join(",") : undefined,
          notgroups: filter.notgroups?.length ? filter.notgroups.join(",") : undefined,
          wallets: filter.wallets?.length ? filter.wallets.join(",") : undefined,
          step: step ? String(step) : undefined,
        }),
    ),

  /**
   * Словарь ручных пометок: какие теги вообще есть и сколько на них кошельков.
   *
   * Отдельный запрос от панели, потому что вопрос другой: панель отвечает «кто в
   * этом окне», а словарь — «чем я вообще помечаю». Он же вход списка фильтра, и
   * спрашивают его на открытие страницы, а не каждые пять секунд.
   */
  walletTags: () => get<WalletTagsResponse>("/wallets/tags"),

  /**
   * Постановка и снятие пометок СРАЗУ НА ГРУППЕ кошельков.
   *
   * Один запрос на всё выделение, а не по кошельку: оператор выделяет строки и
   * вешает пометку разом, и сотня независимых запросов означала бы сотню
   * независимых исходов, половина которых могла бы отказать.
   */
  applyWalletTags: (req: {
    addresses: readonly string[];
    add?: readonly string[];
    remove?: readonly string[];
  }) => request<WalletTagsApplyResponse>("POST", "/wallets/tags", req),

  /**
   * Сделки ОДНОГО кошелька в ЭТОМ ОКНЕ: как он набирал позицию.
   *
   * Не путать с `walletTrades` ниже: там НАШИ сделки с этим кошельком по всем
   * рынкам, из своего журнала, здесь — ВСЕ его сделки в одном окне, у чужого
   * индексатора. Разные вопросы и разные источники.
   *
   * Отдельный запрос, а не поле панели, и это не дробление: панель опрашивается
   * целиком каждые несколько секунд по каждому открытому рынку, а этот список
   * нужен по нажатию на одну строку. Приезжал бы он вместе с панелью — платили
   * бы за него все и всегда.
   */
  marketWalletTrades: (conditionID: string, wallet: string, limit?: number) =>
    get<MarketWalletTradesResponse>(
      `/markets/${encodeURIComponent(conditionID)}/participants/` +
        `${encodeURIComponent(wallet)}/trades` +
        qs({ limit: limit === undefined ? undefined : String(limit) }),
    ),

  /**
   * Свод встреч по КОШЕЛЬКАМ: кто принял наш налив и кто снял нас.
   *
   * Тот же журнал, что у `trades`, но вопрос другой. Там строка на сделку, здесь
   * строка на кошелёк, и свести первое во второе на этой стороне нельзя: страница
   * истории обрезана двумя сотнями сделок, и сумма по кошельку из обрезанного
   * списка была бы неверной, а не неполной.
   *
   * Все умолчания (окно в сутки, окно статистики, потолок) живут на сервере и
   * приезжают эхом в ответе.
   */
  /**
   * Тир и возврат тейкерской комиссии. Выплаты и тир у каждого аккаунта свои,
   * поэтому account обязателен. Окно задано самой программой (30 суток) и
   * ручкой не является: подставить его сюда значило бы предложить оператору
   * выбрать число, которое выбирает биржа.
   */
  rebates: (account: string) => get<RebatesResponse>(`/rebates${qs({ account })}`),

  /**
   * Плотность стакана биржи расчёта. Без параметров: символы задаёт
   * CEX_DEPTH_SYMBOLS на сервере, и спрашивать их по одному незачем — их два.
   */
  cexDepth: () => get<CexDepthResponse>("/cex/depth"),

  /**
   * Сводка фида crypto-console по активу. Отсутствие сводки — не 404, а
   * `summary: null` с причиной: ключ живёт 15 с, и «трейдер не публикует»
   * — штатное состояние, которое панель показывает словами.
   */
  ccfeed: (asset: string) => get<CCFeedResponse>(`/ccfeed/${encodeURIComponent(asset)}`),

  /**
   * Проба predict.fun (ADR-0040). Стакан пары окна — по slug Polymarket;
   * отсутствие — не 404, а `book: null` с причиной, как у сводки фида.
   * Ордера — настоящие деньги на BNB Chain, решение о них принимает сервис
   * predictfun, web_api только передаёт.
   */
  pfBook: (slug: string) => get<PFBookResponse>(`/predict/book/${encodeURIComponent(slug)}`),
  pfStatus: () => get<PFStatus>("/predict/status"),
  pfOrders: (status: "open" | "all") => get<{ orders: PFOrder[] }>(`/predict/orders${qs({ status })}`),
  pfPlace: (req: PFPlaceRequest) => request<PFPlaceResult>("POST", "/predict/orders", req),
  /** Итог ордера долгим опросом: сервис держит запрос до wait секунд. */
  pfResult: (id: string, wait: number) =>
    get<PFOutcome>(`/predict/orders/${encodeURIComponent(id)}/result${qs({ wait })}`),
  pfPositions: (slug: string) => get<{ positions: PFPosition[] }>(`/predict/positions${qs({ slug })}`),
  pfCancelMarket: (slug: string) => request<PFCancelResult>("POST", "/predict/orders/cancel-market", { slug }),
  pfCancel: (id: string) => request<PFCancelResult>("DELETE", `/predict/orders/${encodeURIComponent(id)}`),

  counterparties: (params: {
    // Явные `| undefined`, а не необязательные поля: exactOptionalPropertyTypes
    // различает «ключа нет» и «ключ есть со значением undefined», а фильтры сюда
    // приходят из query-строки, где отсутствие и есть undefined.
    sinceHours: number | undefined;
    market: string | undefined;
    flow: FlowWindow | undefined;
    limit?: number;
  }) =>
    get<CounterpartiesResponse>(
      "/counterparties" +
        qs({
          since_hours: params.sinceHours,
          market: params.market,
          flow: params.flow,
          limit: params.limit,
        }),
    ),

  /**
   * Профиль кошелька-контрагента.
   *
   * 404 здесь — осмысленный ответ, а не ошибка данных: контрагентов система
   * узнаёт только из наших сделок, поэтому «кошелька нет» означает «мы с ним
   * не торговали». Вызывающий обязан показать это словами, а не пустой формой.
   */
  wallet: (address: string) =>
    get<WalletResponse>(`/wallets/${encodeURIComponent(address)}`),

  /** Наши сделки с этим кошельком — из своего журнала, а не у индексатора. */
  walletTrades: (address: string, limit?: number) =>
    get<WalletTradesResponse>(
      `/wallets/${encodeURIComponent(address)}/trades${qs(limit ? { limit } : {})}`,
    ),

  /**
   * История НАШИХ сделок со встречными строками.
   *
   * Не «лента за всё время»: лента — скользящее окно на пару минут (тысяча
   * событий на вкладку при 7 событиях в секунду), и сделка, случившаяся утром,
   * из неё уходит навсегда. Здесь тот же вопрос задаётся журналу.
   */
  trades: (params?: { market?: string; wallet?: string; since_hours?: number; limit?: number }) =>
    get<TradesResponse>(`/trades${qs(params)}`),

  /** Привязки подсистем к аккаунтам (T5.4). */
  subsystemAccounts: () => get<SubsystemAccounts>("/subsystem-accounts"),
  /**
   * Назначить или снять аккаунт подсистем. Частично: тело несёт только
   * изменённые — переключатель одной подсистемы, отправив устаревший снимок
   * остальных, вернул бы привязку, которую в соседней вкладке только что сняли.
   */
  setSubsystemAccounts: (patch: SubsystemAccountsPatch) =>
    request<SubsystemAccounts>("PUT", "/subsystem-accounts", patch),

  killSwitch: () => get<KillSwitches>("/risk/kill-switch"),
  /**
   * Взвести или снять стоп. Без account — общий; с account — стоп одного
   * аккаунта (risk:kill_switch:<funder>), остальные торгуют.
   */
  setKillSwitch: (on: boolean, account?: string) =>
    request<KillSwitches>("POST", "/risk/kill-switch", account ? { on, account } : { on }),

  /**
   * Сказать трейдеру, какие суммы держать пред-подписанными.
   *
   * Направление именно такое: суммы придумывает оператор, а не окружение. Пока
   * набор жил в PRESIGN_SIZES_USD, правка пресетов обнуляла долю попаданий в кэш
   * до следующего выката трейдера — при полностью исправном кэше.
   *
   * Метода чтения здесь нет намеренно: действующий набор приходит в GET /status
   * вместе с остальным, на что оператор смотрит одним взглядом, а второй источник
   * того же факта разошёлся бы с первым.
   */
  setPresignSizes: (req: WarmSizesRequest) =>
    request<WarmSizesResponse>("POST", "/presign/sizes", req),

  // --- авторежим заявки на кромке ------------------------------------------

  /**
   * Разрешение по ОДНОМУ окну: включено ли и на какую сумму.
   *
   * Отдельный маршрут на рынок, а не поле в /markets/{id}: разрешение — это
   * решение оператора, а справочник рынка — факт биржи, и класть их в один
   * ответ значило бы переписывать решение при каждом обновлении справочника.
   */
  automaker: (conditionID: string) =>
    get<MakerArmResponse>(`/markets/${encodeURIComponent(conditionID)}/automaker`),

  setAutomaker: (conditionID: string, req: { enabled: boolean; usd?: string }) =>
    request<MakerArmResponse>(
      "POST",
      `/markets/${encodeURIComponent(conditionID)}/automaker`,
      req,
    ),

  // --- копирование ставок умных кошельков (ADR-0010) -----------------------

  /**
   * Лента сигналов. pending: true — только те, что ждут решения.
   *
   * Признак уезжает строкой, а не булевым: qs собирает query из строк и чисел, и
   * `false` в нём превратился бы в параметр `pending=false`, который сервер
   * прочитал бы как «не строка true», то есть верно, но случайно.
   */
  signals: (params?: { hours?: number; pending?: boolean }) =>
    get<{ signals: FollowSignal[] }>(
      `/follow/signals${qs({
        hours: params?.hours,
        pending: params?.pending ? "true" : undefined,
      })}`,
    ),

  /**
   * Сводка двух гипотез: мейкерский вход против тейкерского.
   *
   * Ради неё работа и затевалась. Мейкерский ордер не платит комиссии, но
   * исполняется ровно тогда, когда цена ВЕРНУЛАСЬ, то есть когда движение кита
   * не подтвердилось; тейкерский входит всегда, но платит. Что дороже — вопрос
   * к измерению, и это ответ на него.
   */
  followSummary: (hours?: number, windowLen?: number) =>
    get<{ summary: FollowSummary; scenarios?: Record<string, FollowSummary> }>(
      `/follow/summary${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),
  /** Режим ОДНОГО сценария: читается и пишется своим ключом. */
  scenarioMode: (id: string) =>
    get<{ scenario: string; follow_mode: FollowModeState }>(
      `/follow/scenarios/${encodeURIComponent(id)}/mode`,
    ),
  setScenarioMode: (id: string, mode: FollowMode) =>
    request<{ scenario: string; follow_mode: FollowModeState }>(
      "POST",
      `/follow/scenarios/${encodeURIComponent(id)}/mode`,
      { mode },
    ),

  /**
   * Живой режим стратегий: настройки, которые выставил ОПЕРАТОР.
   *
   * Действующее значение здесь НЕ считается — это строгий минимум с потолком
   * окружения, и считает его трейдер. Оно приезжает в GET /status полем
   * `strategies`. Второе определение действующего режима на экране разошлось бы
   * с тем, по которому уходят деньги.
   */
  strategySettings: (id: string) =>
    get<{ settings: StrategySettings }>(`/strategies/${encodeURIComponent(id)}/settings`),
  /**
   * Записать настройки. Поля НЕОБЯЗАТЕЛЬНЫ и пишутся поодиночке: оператор
   * меняет сумму, не трогая режим, и роль, не трогая сумму. Отправь мы объект
   * целиком — два нажатия в разных вкладках затирали бы друг друга.
   */
  setStrategySettings: (id: string, patch: StrategySettingsPatch) =>
    request<{ settings: StrategySettings }>(
      "POST",
      `/strategies/${encodeURIComponent(id)}/settings`,
      patch,
    ),
  /** Общий выключатель всех стратегий разом. */
  setStrategyModeAll: (mode: StrategyMode) =>
    request<{ mode: StrategyMode }>("POST", "/strategies/mode", { mode }),
  /**
   * Взять строку журнала ролью kind — ступень `alert`.
   *
   * Цены в теле НЕТ: план пересчитывает трейдер из живого стакана в момент
   * команды. Сумма едет ради СВЕРКИ — трейдер отвергнет команду, если
   * действующая успела измениться в соседней вкладке.
   */
  takeStrategySignal: (
    id: string,
    signal: string,
    kind: "maker" | "taker",
    account: string,
    budgetUSD?: string,
  ) =>
    // Только account, без когорты: сигнал забирается один раз, и N команд на
    // одну заявку подрались бы за неё (web_api отвечает 400).
    request<{ command: CommandAccepted }>(
      "POST",
      `/strategies/${encodeURIComponent(id)}/signals/${encodeURIComponent(signal)}/take`,
      { kind, budget_usd: budgetUSD ?? "", account },
    ),
  /** Закрыть строку без ордера: «посмотрел и решил не идти» — тоже факт. */
  skipStrategySignal: (id: string, signal: string) =>
    request<{ command: CommandAccepted }>(
      "POST",
      `/strategies/${encodeURIComponent(id)}/signals/${encodeURIComponent(signal)}/skip`,
      {},
    ),

  /**
   * Выставить ордер по сигналу.
   *
   * Тело НЕ содержит параметров ордера, и это главное здесь: цену, размер, актив
   * и срок берёт сервер из своей строки журнала. Иначе кнопка «выставить $10»
   * стала бы кнопкой «выставить сколько угодно».
   *
   * 409 означает «поздно»: сигнал сгорел, уже отработан либо по этому окну ордер
   * уже стоит. Тексты у этих случаев разные — показывать надо тот, что пришёл.
   */
  placeSignal: (id: string, account: string) =>
    request<{ command: CommandAccepted }>(
      "POST",
      `/follow/signals/${encodeURIComponent(id)}/place`,
      { account },
    ),

  /** Пропустить сигнал. Отдельное действие, а не молчаливое протухание карточки. */
  skipSignal: (id: string) =>
    request<{ signal?: FollowSignal }>(
      "POST",
      `/follow/signals/${encodeURIComponent(id)}/skip`,
      {},
    ),

  followMode: () => get<{ follow_mode: FollowModeState }>("/follow/mode"),
  /**
   * Выставить режим.
   *
   * Поднять выше потолка FOLLOW_MODE это не даст: действующий режим считает
   * трейдер как минимум двух источников. Ответ показывает, что записано, а не
   * что применится, — и интерфейс обязан говорить это вслух.
   */
  setFollowMode: (mode: FollowMode) =>
    request<{ follow_mode: FollowModeState }>("POST", "/follow/mode", { mode }),

  // --- операционные тревоги (веха M8) ---------------------------------------

  /**
   * Висящие операционные тревоги и кольцо последних событий.
   *
   * Отвечает ИЗ ПАМЯТИ движка: ни Redis, ни Postgres в запросе не участвуют.
   * Это не оптимизация, а требование — эндпоинт обязан работать ровно в тот
   * момент, когда база или шина мертвы, потому что именно тогда он и нужен.
   *
   * Как и у замка, ответ несёт known: пустой список при known=false означает
   * «тревоги выключены», а не «всё в порядке».
   */
  opsAlerts: () => get<OpsAlertsResponse>("/ops/alerts"),

  // --- замок исхода окна ---------------------------------------------------

  /**
   * Висящие карточки замка.
   *
   * Ответ несёт признак known: пустой список при known=false означает «подсистема
   * молчит», а не «замков сейчас нет». Разница решающая — второе оператор ждёт
   * спокойно, первое чинит.
   */
  lockSignals: () => get<LockSignalsResponse>("/lock/signals"),

  /**
   * Взять карточку ролью kind.
   *
   * Тело НЕ содержит цены и размера, и это главное здесь: план пересчитывает
   * трейдер из ЖИВОГО стакана в момент команды. У web_api живого стакана нет
   * вовсе (ключ book:<asset_id> переписывается раз в десять секунд), и цена
   * отсюда была бы ценой десятисекундной давности на рынке, который решается за
   * секунды.
   *
   * budget_usd едет не ради счёта, а ради сверки: трейдер отвергнет команду,
   * если действующая сумма успела измениться в соседней вкладке.
   */
  takeLock: (id: string, kind: LockKind, account: string, budgetUSD?: string) =>
    // Конверт {command}, как у всех командных ручек: ключ, по которому
    // интерфейс снимает «отправляется», — trace_id. Прежде здесь был объявлен
    // {client_order_id, kind}, и объявление врало дважды: сервер писал ДВА
    // склеенных тела, а res.json() падал на втором, превращая ушедшую команду
    // в красное «не удалось отправить».
    request<{ command: CommandAccepted }>(
      "POST",
      `/lock/signals/${encodeURIComponent(id)}/take`,
      { kind, account, ...(budgetUSD ? { budget_usd: budgetUSD } : {}) },
    ),

  /** Пропустить карточку. Отдельное действие, а не молчаливое протухание. */
  skipLock: (id: string) =>
    request<{ command: CommandAccepted }>(
      "POST",
      `/lock/signals/${encodeURIComponent(id)}/skip`,
      {},
    ),

  lockSettings: () => get<{ lock: LockSettings }>("/lock/settings"),
  /**
   * Выставить режим порога и/или сумму сделки.
   *
   * Оба поля необязательны: панель меняет то, что нажали. Сумму трейдер зажимает
   * потолком LOCK_ORDER_USD — кнопкой её можно только уменьшить.
   */
  setLockSettings: (req: { mode?: LockMode; order_usd?: string }) =>
    request<{ lock: LockSettings }>("POST", "/lock/settings", req),

  /**
   * Сводка теневого сигнала «покупка почти решённого» по двум источникам.
   *
   * Пишущих ручек у раздела нет и не будет, пока у подсистемы нет порта
   * постановки ордера: кнопка по строке, за которой не может стоять ордер,
   * обещала бы действие, которого не существует.
   */
  decidedSummary: (hours?: number, windowLen?: number) =>
    // fresh_book_ms — порог свежести снимка стакана, которым посчитаны колонки
    // результата. Едет с сервера, а не задан в браузере: подпись под таблицей
    // обязана называть то же число, каким числа и посчитаны.
    get<{ hours: number; sources: DecidedSource[]; fresh_book_ms: number }>(
      `/decided/summary${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /** Лента теневых входов. Пустой источник означает «оба». */
  decidedSignals: (hours?: number, source?: string, limit?: number, windowLen?: number) =>
    get<{ signals: DecidedSignal[] }>(
      `/decided/signals${qs({
        ...(hours ? { hours } : {}),
        ...(source ? { source } : {}),
        ...(limit ? { limit } : {}),
        ...winQS(windowLen),
      })}`,
    ),

  /**
   * Сводка теневого сигнала «расхождение модели и стакана» по паре
   * «источник × роль».
   *
   * Пишущих ручек у раздела нет и не будет по тому же доводу, что у почти
   * решённого: у подсистемы нет порта постановки ордера.
   */
  mispricedSummary: (hours?: number, windowLen?: number) =>
    get<{ hours: number; groups: MispricedGroup[] }>(
      `/mispriced/summary${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /**
   * Разрез счёта расхождения по минуте захода и цене плана.
   *
   * Отдельной ручкой, а не полем сводки: читается только по развёрнутой строке
   * панели, и в такте опроса сводки ему делать нечего.
   */
  mispricedBreakdown: (hours?: number, windowLen?: number) =>
    get<{ hours: number; slices: MispricedSlice[] }>(
      `/mispriced/breakdown${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /**
   * Сетка порога кромки: что дала бы каждая ступень MISPRICED_TAKER_MARGIN.
   *
   * Отдельной ручкой, а не полем сводки: читается только под развёрнутой
   * панелью сетки, а в сводку приезжала бы каждым тактом опроса — под сотню
   * клеток ради таблицы, которую чаще всего никто не открывал.
   */
  mispricedGrid: (hours?: number, windowLen?: number) =>
    get<MispricedGridResponse>(
      `/mispriced/grid${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /** Лента теневых заявок. Пустые источник и роль означают «все». */
  mispricedSignals: (
    hours?: number,
    source?: string,
    role?: string,
    limit?: number,
    windowLen?: number,
  ) =>
    get<{ signals: MispricedSignal[] }>(
      `/mispriced/signals${qs({
        ...(hours ? { hours } : {}),
        ...(source ? { source } : {}),
        ...(role ? { role } : {}),
        ...(limit ? { limit } : {}),
        ...winQS(windowLen),
      })}`,
    ),

  /**
   * Форма счёта по ВСЕМ теневым журналам разом.
   *
   * Один запрос на страницу, а не по одному на панель: вопрос «из чего сложился
   * счёт» ко всем подсистемам один и тот же, границы корзин общие, и шесть ручек
   * развели бы их на первой правке.
   */
  shadowShapes: (hours?: number, windowLen?: number) =>
    get<{ hours: number; journals: Record<string, ShadowShape[]> }>(
      `/shadow/shapes${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /** Сводка теневой ставки по перевесу группы, разрезом по минуте окна. */
  cohortSummary: (hours?: number, windowLen?: number) =>
    get<{ hours: number; buckets: CohortBucket[] }>(
      `/cohort/summary${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /**
   * Лента теневых ставок по перевесу группы.
   *
   * `counted` не передаётся по умолчанию НАМЕРЕННО: лента отвечает на вопрос
   * «что подсистема видела», а не «что попало в зачёт», и спрятанные отказы
   * сделали бы её бесполезной ровно тогда, когда правило съедает поток.
   */
  cohortSignals: (
    hours?: number,
    limit?: number,
    countedOnly?: boolean,
    windowLen?: number,
  ) =>
    get<{ signals: CohortSignal[] }>(
      `/cohort/signals${qs({
        ...(hours ? { hours } : {}),
        ...(limit ? { limit } : {}),
        ...(countedOnly ? { counted: 1 } : {}),
        ...winQS(windowLen),
      })}`,
    ),

  /**
   * Сводка теневой ставки по КОТИРОВКЕ группы — по клеткам сетки и минутам окна.
   *
   * Ответ несёт `nested: true`, и это не украшение: клетки вложены, и сумма по
   * столбцу посчитает строку многократно. Складывать их на этой стороне нельзя.
   */
  consensusSummary: (hours?: number, windowLen?: number) =>
    get<{
      hours: number;
      cells: ConsensusCell[];
      nested: boolean;
      calibration: ConsensusBin[];
      taker_by_ask: ConsensusTakerBucket[];
    }>(
      `/consensus/summary${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /**
   * Отчёт о тейкерской гипотезе одной клетки воронкой. Пустой `cell` — главная.
   */
  consensusTaker: (hours?: number, cell?: string) =>
    get<ConsensusTakerReport>(
      `/consensus/taker${qs({ ...(hours ? { hours } : {}), ...(cell ? { cell } : {}) })}`,
    ),

  /**
   * Лента теневых ставок по котировке группы.
   *
   * `counted` не передаётся по умолчанию по тому же доводу, что у соседей:
   * лента отвечает на вопрос «что подсистема видела», а не «что попало в зачёт».
   */
  consensusSignals: (hours?: number, limit?: number, countedOnly?: boolean) =>
    get<{ signals: ConsensusSignal[] }>(
      `/consensus/signals${qs({
        ...(hours ? { hours } : {}),
        ...(limit ? { limit } : {}),
        ...(countedOnly ? { counted: 1 } : {}),
      })}`,
    ),

  /**
   * Сводка теневой покупки смещённого края по клеткам «порог × минута».
   *
   * Пишущих ручек у раздела нет и не будет по тому же доводу, что у соседей:
   * у подсистемы нет порта постановки ордера, и кнопка по строке, за которой не
   * может стоять ордер, обещала бы действие, которого не существует.
   */
  shiftSummary: (hours?: number, windowLen?: number) =>
    get<{ hours: number; cells: ShiftCell[] }>(
      `/shift/summary${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /**
   * Разрез клетки смещения по «подтверждение расхождением × разворот».
   *
   * Отдельной ручкой, а не полем сводки: читается только по развёрнутой строке,
   * а клеток здесь полсотни — в такте опроса сводки двумстам корзинам делать
   * нечего. `confirm_s` приезжает оттуда же: ширину окна подтверждения называет
   * подпись на экране, и вторая её копия в браузере разошлась бы с сервером
   * молча при первой калибровке.
   */
  shiftBreakdown: (hours?: number, windowLen?: number) =>
    get<{ hours: number; confirm_s: number; slices: ShiftSlice[] }>(
      `/shift/breakdown${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  /** Лента теневых входов. Пустая ступень означает «вся лесенка». */
  shiftSignals: (hours?: number, thresholdBP?: number, limit?: number, windowLen?: number) =>
    get<{ signals: ShiftSignal[] }>(
      `/shift/signals${qs({
        ...(hours ? { hours } : {}),
        ...(thresholdBP ? { threshold_bp: thresholdBP } : {}),
        ...(limit ? { limit } : {}),
        ...winQS(windowLen),
      })}`,
    ),

  /**
   * Сводка теневой покупки пары дешевле доллара по ролям.
   *
   * Пишущих ручек у раздела нет и не будет по тому же доводу, что у соседей: у
   * подсистемы нет порта постановки ордера.
   */
  pairSummary: (hours?: number, windowLen?: number) =>
    get<{ hours: number; roles: PairRoleSummary[] }>(
      `/pair/summary${qs({ ...(hours ? { hours } : {}), ...winQS(windowLen) })}`,
    ),

  // --- ИИ-судья (ADR-0038) ------------------------------------------------

  /**
   * Вопрос «ставить или не ставить» обоим провайдерам. ВОПРОС, а не ордер:
   * уходит стримом cmd.ai, и у судьи нет порта постановки. Бюджет — только
   * масштаб теневого счёта.
   */
  askAI: (conditionID: string, budgetUSD: string) =>
    request<{ ask_id: string; requested_at: string }>("POST", "/ai/asks", {
      condition_id: conditionID,
      budget_usd: budgetUSD,
    }),
  /** Запрос целиком: снимок, текст входа, ответы обоих провайдеров. */
  aiAsk: (id: string) => get<AIAskView>(`/ai/asks/${encodeURIComponent(id)}`),
  /** История запросов по рынку. */
  aiAsks: (conditionID: string, limit?: number) =>
    get<{ asks: AIAskView[] }>(
      `/ai/asks${qs({ condition_id: conditionID, ...(limit ? { limit } : {}) })}`,
    ),
  /** Сводка сравнения провайдеров. */
  aiSummary: (hours?: number, minConfidence?: number) =>
    get<{ hours: number; summary: AISummary }>(
      `/ai/summary${qs({
        ...(hours ? { hours } : {}),
        ...(minConfidence !== undefined ? { min_confidence: minConfidence } : {}),
      })}`,
    ),

  /** Лента теневых пар. Пустая роль означает «обе». */
  pairSignals: (hours?: number, role?: string, limit?: number, windowLen?: number) =>
    get<{ signals: PairSignal[] }>(
      `/pair/signals${qs({
        ...(hours ? { hours } : {}),
        ...(role ? { role } : {}),
        ...(limit ? { limit } : {}),
        ...winQS(windowLen),
      })}`,
    ),

  /**
   * Сводка консенсуса семьи кошельков: общий итог и разбивка по семьям одним
   * ответом — вопрос «какая из семей пула вообще даёт кромку» без разбивки не
   * отвечается.
   *
   * Пишущих ручек у раздела нет и не будет: у подсистемы нет порта постановки
   * ордера, и кнопка по строке, за которой не может стоять ордер, обещала бы
   * действие, которого не существует.
   */
  familySummary: (hours?: number, rev?: number, windowLen?: number) =>
    get<{
      hours: number;
      rev: number;
      revisions: FamilyRevRows[];
      summary: FamilySummary;
      families: Record<string, FamilySummary>;
    }>(
      `/family/summary${qs({
        ...(hours ? { hours } : {}),
        ...(rev ? { rev } : {}),
        ...winQS(windowLen),
      })}`,
    ),

  /** Лента сигналов консенсуса. Пустой family_id означает «все семьи». */
  familySignals: (
    hours?: number,
    limit?: number,
    familyID?: string,
    rev?: number,
    windowLen?: number,
  ) =>
    get<{ signals: FamilySignal[] }>(
      `/family/signals${qs({
        ...(hours ? { hours } : {}),
        ...(limit ? { limit } : {}),
        ...(familyID ? { family_id: familyID } : {}),
        ...(rev ? { rev } : {}),
        ...winQS(windowLen),
      })}`,
    ),

  /**
   * Состав пула на одну ревизию вместе с уликами связи. Без `rev` — ПОСЛЕДНЯЯ
   * ревизия; с `rev` — та, под которой снята конкретная строка ленты.
   */
  familyPool: (rev?: number) =>
    get<{ roster: FamilyRoster; links: FamilyLink[] }>(
      `/family/pool${qs(rev ? { rev } : {})}`,
    ),
};
